import { getAuthenticatedSupabase } from '../lib/supabaseClient';
import { useAuth } from '@clerk/clerk-react';

export interface SubscriptionData {
  user_id: string;
  tier: string;
  status: 'active' | 'cancelled' | 'past_due';
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  stripe_price_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExportStats {
  user_id: string;
  tier: string;
  current_month: string;
  exports_this_month: number;
  total_exports: number;
  last_export_at?: string;
  created_at: string;
  updated_at: string;
}

export class SubscriptionService {
  // Handle successful payment completion
  static async handlePaymentSuccess(userId: string, tier: string, subscriptionId?: string, getToken?: () => Promise<string | null>) {
    try {
      console.log('✅ Handling payment success for user:', userId, 'tier:', tier);

      // Get authenticated Supabase client with fallback
      let supabase;
      try {
        if (getToken) {
          supabase = await getAuthenticatedSupabase(getToken);
        } else {
          throw new Error('No token function provided');
        }
      } catch (authError) {
        console.warn('⚠️ Authentication failed, using fallback Supabase client:', authError);
        // Use fallback client without authentication
        supabase = (await import('../lib/supabaseClient')).supabase;
      }

      // First, check if subscription already exists
      const { data: existingSubscription } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (existingSubscription) {
        // Update existing subscription
        const { error: subscriptionError } = await supabase
          .from('subscriptions')
          .update({
            stripe_subscription_id: subscriptionId,
            tier: tier,
            status: 'active',
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (subscriptionError) {
          console.error('❌ Error updating subscription:', subscriptionError);
          throw subscriptionError;
        }
      } else {
        // Create new subscription
        const { error: subscriptionError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            stripe_subscription_id: subscriptionId,
            tier: tier,
            status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (subscriptionError) {
          console.error('❌ Error creating subscription:', subscriptionError);
          throw subscriptionError;
        }
      }

      // Reset export stats for new tier
      const currentMonth = new Date().toISOString().slice(0, 7); // Format: 2025-10
      const { error: exportError } = await supabase
        .from('user_export_stats')
        .upsert({
          user_id: userId,
          tier: tier,
          current_month: currentMonth,
          exports_this_month: 0,
          total_exports: 0,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,current_month'
        });

      if (exportError) {
        console.error('❌ Error updating export stats:', exportError);
        throw exportError;
      }

      console.log('✅ Subscription data persisted successfully');
      return { 
        success: true, 
        message: 'Payment successful! Your subscription is now active.',
        tier: tier,
        databaseUpdated: true
      };
    } catch (error) {
      console.error('❌ Error handling payment success:', error);
      throw error;
    }
  }

  // Handle payment failure
  static async handlePaymentFailure(userId: string, getToken?: () => Promise<string | null>) {
    try {
      console.log('❌ Handling payment failure for user:', userId);

      if (!getToken) {
        throw new Error('No token function provided');
      }

      // Get authenticated Supabase client
      const supabase = await getAuthenticatedSupabase(getToken);

      // Downgrade to free tier
      const { error: subscriptionError } = await supabase
        .from('subscriptions')
        .update({
          tier: 'free',
          status: 'past_due',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (subscriptionError) {
        console.error('❌ Error updating subscription:', subscriptionError);
        throw subscriptionError;
      }

      // Update export stats to free tier limits
      const { error: exportError } = await supabase
        .from('user_export_stats')
        .update({
          tier: 'free',
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (exportError) {
        console.error('❌ Error updating export stats:', exportError);
        throw exportError;
      }

      console.log('✅ User downgraded to free tier');
      return { 
        success: true, 
        message: 'Payment failed. You have been downgraded to free tier.',
        databaseUpdated: true
      };
    } catch (error) {
      console.error('❌ Error handling payment failure:', error);
      throw error;
    }
  }

  // Get user's current subscription
  static async getCurrentSubscription(userId: string, getToken?: () => Promise<string | null>): Promise<SubscriptionData | null> {
    try {
      // Get authenticated Supabase client with fallback
      let supabase;
      try {
        if (getToken) {
          supabase = await getAuthenticatedSupabase(getToken);
        } else {
          throw new Error('No token function provided');
        }
      } catch (authError) {
        console.warn('⚠️ Authentication failed, using fallback Supabase client:', authError);
        // Use fallback client without authentication
        supabase = (await import('../lib/supabaseClient')).supabase;
      }
      
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error fetching subscription:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Error getting current subscription:', error);
      return null;
    }
  }

  // Get user's export stats
  static async getExportStats(userId: string, getToken?: () => Promise<string | null>): Promise<ExportStats | null> {
    try {
      // Get authenticated Supabase client with fallback
      let supabase;
      try {
        if (getToken) {
          supabase = await getAuthenticatedSupabase(getToken);
        } else {
          throw new Error('No token function provided');
        }
      } catch (authError) {
        console.warn('⚠️ Authentication failed, using fallback Supabase client:', authError);
        // Use fallback client without authentication
        supabase = (await import('../lib/supabaseClient')).supabase;
      }
      
      const { data, error } = await supabase
        .from('user_export_stats')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('❌ Error fetching export stats:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Error getting export stats:', error);
      return null;
    }
  }

  // Helper function to get tier limits (signature exports per month)
  static getTierLimit(tier: string): number {
    const limits = {
      free: 5,
      walletCard: 0, // No signature exports for wallet card tier
      starter: 10,
      pro: 50,
      business: 200,
      enterprise: 1000
    };
    return limits[tier as keyof typeof limits] || 5;
  }

  // Create mock subscription data for display when database fails
  static createMockSubscriptionData(tier: string): SubscriptionData {
    return {
      user_id: 'temp_user',
      tier: tier,
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }

  // Create mock export stats for display when database fails
  static createMockExportStats(tier: string): ExportStats {
    const currentMonth = new Date().toISOString().slice(0, 7); // Format: 2025-10
    return {
      user_id: 'temp_user',
      tier: tier,
      current_month: currentMonth,
      exports_this_month: 0,
      total_exports: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
  }
} 